package com.arena_web.ui.sections.eventdetail

import androidx.compose.runtime.Composable
import com.arena_web.core.viewmodel.EventDetailUiState
import com.arena_web.core.viewmodel.UserParticipation
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.sections.arena.EventStatus
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import com.arena_web.utils.openInMaps
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import kotlin.js.Date

@Composable
fun MatchInfoBanner(
    state: EventDetailUiState,
    onJoinClick: () -> Unit = {},
    onLeaveClick: () -> Unit = {},
    onStartClick: () -> Unit = {},
    onEditClick: () -> Unit = {},
    onDeleteClick: () -> Unit = {},
    modifier: Modifier = Modifier
) {
    val sportColors = LocalSportColors.current
    val t = LocalAppStrings.current
    val event = state.event ?: return

    val eventStatus = when (event.status.uppercase()) {
        "IN_PROGRESS" -> EventStatus.IN_PROGRESS
        "COMPLETED", "FINISHED" -> EventStatus.COMPLETED
        "CANCELED", "CANCELLED" -> EventStatus.CANCELED
        else -> EventStatus.NEW
    }

    val parsed = Date(event.dateTime)
    val months = arrayOf(
        t.eventMonthJanuary, t.eventMonthFebruary, t.eventMonthMarch, t.eventMonthApril,
        t.eventMonthMay, t.eventMonthJune, t.eventMonthJuly, t.eventMonthAugust,
        t.eventMonthSeptember, t.eventMonthOctober, t.eventMonthNovember, t.eventMonthDecember
    )
    val dateStr = "${parsed.getDate()} ${months[parsed.getMonth()]}"
    val timeStr = "${parsed.getHours().toString().padStart(2, '0')}:${parsed.getMinutes().toString().padStart(2, '0')}"

    val title = event.title

    // Top-right slot: PRO badge is always visible for Pro events, admin icons
    // sit to its right when the viewer can edit/delete the event.
    val showAdminIcons = state.isAdmin && eventStatus == EventStatus.NEW
    val showProBadge = event.isPro

    Box(
        modifier = modifier
            .fillMaxWidth()
            .sHeight(404)
            .sBorderRadius(22)
            .overflow(Overflow.Hidden)
            .position(Position.Relative)
            .styleModifier {
                property(
                    "background",
                    "url('${event.imageUrl ?: "/icons/event_football_field.png"}') center/cover no-repeat"
                )
            }
    ) {
        // Left-side dark gradient overlay
        Box(
            modifier = Modifier
                .sWidth(540)
                .fillMaxHeight()
                .position(Position.Absolute)
                .top(0.px).left(0.px)
                .styleModifier {
                    property("background-image", AppGradients.OverlayPartialGradient)
                }
        )

        // Match Info card — semi-transparent overlay (Figma 8836:329307).
        // Border ring is painted by an absolute overlay using the project's
        // mask-composite trick (same as MessageInputBar) so the gradient
        // outline doesn't conflict with the card's backdrop-filter background.
        Column(
            modifier = Modifier
                .position(Position.Absolute)
                .styleModifier {
                    property("left", s(22))
                    property("top", s(46))
                    property("width", s(538))
                    property("background", "rgba(2, 4, 11, 0.4)")
                    property("backdrop-filter", "blur(4px)")
                }
                .sBorderRadius(22)
                .sPadding(22, 24)
                .sGap(40)
        ) {
            Box(
                modifier = Modifier.styleModifier {
                    property("position", "absolute")
                    property("inset", "0")
                    property("border", "0.3px solid transparent")
                    property("border-radius", s(22))
                    property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                    property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                    property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                    property("-webkit-mask-composite", "xor")
                    property("mask-composite", "exclude")
                    property("pointer-events", "none")
                }
            )

            // Title row — title + (optional PRO badge + admin icons), gap 60.
            // Pending-approval badge stacks under the PRO badge, or takes its
            // slot if the event isn't Pro, so the "waiting" state lives in the
            // same top-right anchor as the other status chrome.
            val showPendingBadge = state.userParticipation == UserParticipation.PENDING
            Row(
                modifier = Modifier.fillMaxWidth().sGap(60),
                verticalAlignment = Alignment.Top,
            ) {
                SpanText(
                    title,
                    modifier = Modifier
                        .flexGrow(1)
                        .sFontSize(26)
                        .fontWeight(FontWeight.SemiBold)
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                        .styleModifier {
                            property("line-height", s(30))
                            property("letter-spacing", s(0.26))
                            property("white-space", "nowrap")
                            property("overflow", "hidden")
                            property("text-overflow", "ellipsis")
                        }
                )
                if (showProBadge || showAdminIcons) {
                    Row(
                        modifier = Modifier.sGap(8),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        if (showProBadge) ProBadge(sportColors = sportColors)
                        if (showAdminIcons) {
                            IconActionButton(AppIcons.Pencil, onClick = onEditClick)
                            IconActionButton(AppIcons.Trash, onClick = onDeleteClick)
                        }
                    }
                }
            }

            // Main frame — info column on the left, action button bottom-right.
            // The info column flexes/shrinks (min-width:0 lets the address chip
            // ellipsis) while the action column is pinned (flex-shrink:0) so a
            // long address can never push the buttons past the card's edge.
            Row(
                modifier = Modifier.fillMaxWidth().sGap(16),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.Bottom,
            ) {
                Box(
                    modifier = Modifier.styleModifier {
                        property("flex", "1 1 0")
                        property("min-width", "0")
                    }
                ) {
                    InfoColumn(event = event, timeStr = timeStr, dateStr = dateStr)
                }

                Column(
                    modifier = Modifier.styleModifier {
                        property("align-self", "stretch")
                        property("flex-shrink", "0")
                    },
                    horizontalAlignment = Alignment.End,
                    verticalArrangement = Arrangement.SpaceBetween,
                ) {
                    if (showPendingBadge) PendingApprovalBadge() else Box {}
                    PrimaryActionButton(
                        state = state,
                        eventStatus = eventStatus,
                        sportColors = sportColors,
                        onJoinClick = onJoinClick,
                        onLeaveClick = onLeaveClick,
                        onStartClick = onStartClick,
                    )
                }
            }
        }
    }
}

@Composable
private fun InfoColumn(
    event: com.arena_web.data.dto.EventDetailDto,
    timeStr: String,
    dateStr: String,
) {
    val t = LocalAppStrings.current
    // Two info groups separated by gap-24; inside each group rows sit at gap-8.
    Column(modifier = Modifier.fillMaxWidth().sGap(24)) {
        // Group 1 — date/time/location.
        Column(modifier = Modifier.fillMaxWidth().sGap(8)) {
            Row(modifier = Modifier.sGap(18)) {
                InfoChip(icon = AppIcons.Time, text = timeStr)
                InfoChip(icon = AppIcons.Calendar, text = dateStr)
            }
            // Address fills the remaining width and ellipsises — the action
            // column is pinned, so this never overflows the banner.
            InfoChip(
                icon = AppIcons.Location,
                text = event.address,
                fillWidth = true,
                onClick = { openInMaps(event.latitude, event.longitude, event.address) },
            )
        }

        // Group 2 — sport, format (+ teams when Pro), players count + cost.
        Column(modifier = Modifier.sGap(8)) {
            // Sport chip — uses the user-typed name for "Other" custom sports
            // so an `Other / customSportName = "Skateboard"` event reads as
            // "Skateboard" instead of the placeholder bucket name.
            val sportLabel = event.customSportName?.takeIf { it.isNotBlank() } ?: event.sportName
            val format = event.format
            Row(
                modifier = Modifier.sGap(8),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                InfoChip(icon = AppIcons.ballSmall(event.sportName), text = sportLabel)
                // Format chip applies to every event with a format set, not
                // just Pro ones — a casual 5v5 still wants the "5 vs 5"
                // pill visible on the banner.
                if (format != null) {
                    BulletDot()
                    SpanText(
                        t.eventFormatVs(format),
                        modifier = Modifier
                            .sFontSize(18)
                            .fontWeight(FontWeight.Medium)
                            .color(com.arena_web.ui.theme.ThemeVars.Text2)
                            .styleModifier { property("letter-spacing", s(0.09)) },
                    )
                }
                // Teams count stays Pro-only — non-Pro events don't carry
                // pre-formed squads, so the metric would always read "0 teams".
                if (event.isPro) {
                    BulletDot()
                    SpanText(
                        t.eventTeamsCount(event.teams.size),
                        modifier = Modifier
                            .sFontSize(18)
                            .fontWeight(FontWeight.Medium)
                            .color(com.arena_web.ui.theme.ThemeVars.Text2)
                            .styleModifier { property("letter-spacing", s(0.09)) },
                    )
                }
            }

            // Players + (optional) cost share one row, separated by a bullet
            // dot — Figma layout shows wallet pill right after the people pill.
            // Pending-approval participants are excluded from the count so the
            // "X / Y players" pill reflects the locked-in roster.
            val joined = event.participants.count { it.status.uppercase() == "CONFIRMED" }
            val max = event.maxPlayers
            val playersText = if (max != null && max > 0) t.eventPlayersOfMax(joined, max) else t.eventPlayersCount(joined)

            val costNumber = event.cost.trim().toDoubleOrNull()
            val showCost = costNumber != null && costNumber > 0.0
            val costFormatted = if (showCost) {
                if (costNumber!! % 1.0 == 0.0) costNumber.toInt().toString() else event.cost.trim()
            } else null

            Row(
                modifier = Modifier.sGap(8),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                InfoChip(icon = AppIcons.People, text = playersText)
                if (showCost) {
                    BulletDot()
                    InfoChip(
                        icon = AppIcons.Wallet,
                        text = "€$costFormatted",
                        iconSize = 18,
                    )
                }
            }
        }
    }
}

@Composable
private fun InfoChip(
    icon: String,
    text: String,
    iconSize: Int? = null,
    fillWidth: Boolean = false,
    onClick: (() -> Unit)? = null,
) {
    Row(
        modifier = (if (fillWidth) Modifier.fillMaxWidth() else Modifier).sGap(6)
            .thenIf(onClick != null) { Modifier.cursor(Cursor.Pointer).onClick { onClick!!() } },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Icon(
            src = icon,
            modifier = (if (iconSize != null) Modifier.sSize(iconSize) else Modifier).flexShrink(0),
        )
        SpanText(
            text,
            modifier = Modifier
                .sFontSize(18)
                .fontWeight(FontWeight.Medium)
                .color(com.arena_web.ui.theme.ThemeVars.Text2)
                .styleModifier {
                    property("letter-spacing", s(0.09))
                    if (fillWidth) {
                        // Wrap a long address onto a second line, then clamp +
                        // ellipsise past two lines so it never grows the banner.
                        property("display", "-webkit-box")
                        property("-webkit-line-clamp", "2")
                        property("-webkit-box-orient", "vertical")
                        property("overflow", "hidden")
                        property("min-width", "0")
                        property("flex", "1 1 0")
                    }
                }
        )
    }
}

@Composable
private fun BulletDot() {
    Box(
        modifier = Modifier
            .sSize(6)
            .sBorderRadius(100)
            .backgroundColor(AppColors.Text.Secondary)
    )
}

@Composable
private fun ProBadge(sportColors: SportColorScheme) {
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier
            .sBorderRadius(100)
            .sPadding(6, 12)
            .sGap(4)
            .styleModifier {
                // Match the mobile badge: badge fill (top) over the page blue tint
                // over the var(--l-s3, #02040B) base — reads cleanly over any banner photo.
                property("background-color", "var(--arena-bg)")
                property(
                    "background-image",
                    "linear-gradient(rgba(60,121,241,0.18), rgba(60,121,241,0.18)), " +
                        "linear-gradient(0deg, rgba(60,121,241,0) 0%, " +
                        "rgba(60,121,241,0.072) 64%, rgba(60,121,241,0.072) 100%)",
                )
            },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(
            modifier = Modifier
                .sSize(14)
                .backgroundColor(sportColors.primary)
                .styleModifier {
                    property("mask-image", "url('${AppIcons.Diamond}')")
                    property("-webkit-mask-image", "url('${AppIcons.Diamond}')")
                    property("mask-size", "contain")
                    property("-webkit-mask-size", "contain")
                    property("mask-position", "center")
                    property("-webkit-mask-position", "center")
                    property("mask-repeat", "no-repeat")
                    property("-webkit-mask-repeat", "no-repeat")
                }
        )
        SpanText(
            t.eventProBadge,
            modifier = Modifier
                .sFontSize(14)
                .fontWeight(FontWeight.SemiBold)
                .color(sportColors.primary)
        )
    }
}

@Composable
private fun PrimaryActionButton(
    state: EventDetailUiState,
    eventStatus: EventStatus,
    sportColors: SportColorScheme,
    onJoinClick: () -> Unit,
    onLeaveClick: () -> Unit,
    onStartClick: () -> Unit
) {
    val t = LocalAppStrings.current
    when {
        eventStatus == EventStatus.IN_PROGRESS -> {
            // Non-button indicator: red dot + "Live" text on the surface.
            Row(
                modifier = Modifier.sGap(8),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Box(
                    modifier = Modifier
                        .sSize(8)
                        .borderRadius(50.percent)
                        .backgroundColor(Color.rgb(242, 85, 85))
                )
                SpanText(
                    t.eventStatusLive,
                    modifier = Modifier
                        .bodyLMedium()
                        .color(com.arena_web.ui.theme.ThemeVars.Text1)
                )
            }
        }
        eventStatus == EventStatus.COMPLETED -> {
            Box(
                modifier = Modifier
                    .sHeight(44).sBorderRadius(22).sPadding(10, 24)
                    .backgroundColor(Color.rgb(52, 213, 36)),
                contentAlignment = Alignment.Center
            ) {
                SpanText(t.eventCompletedBadge, modifier = Modifier.bodyLMedium().color(Color.rgb(0, 0, 0)))
            }
        }
        eventStatus != EventStatus.NEW -> Unit
        state.isAdmin -> {
            // Admins normally can't leave (the event needs at least one
            // admin to operate). When there's a co-admin to inherit the
            // event, surface Leave alongside Start so the user can hand it
            // off — same gating logic the backend enforces.
            val currentUserId = Storage.get(StorageKeys.USER_ID)
            val hasOtherAdmin = state.event?.participants
                ?.any { it.isAdmin && it.userId != currentUserId } == true
            if (hasOtherAdmin) {
                // Stacked vertically (Start on top, Leave below) — same as the
                // mobile action column, right-aligned by the parent.
                Column(
                    modifier = Modifier.sGap(8),
                    horizontalAlignment = Alignment.End,
                ) {
                    ActionPill(
                        text = t.eventStart,
                        bg = sportColors.primary,
                        textColor = sportColors.onPrimary,
                        trailingIconBg = sportColors.secondary,
                        trailingIcon = AppIcons.ArrowUpOutline,
                        iconTransform = "rotate(45deg)",
                        iconFilter = sportColors.iconFilterOnSecondary,
                        onClick = onStartClick,
                    )
                    ActionPill(
                        text = t.eventLeave,
                        bg = Color.rgb(242, 85, 85),
                        textColor = Color.rgb(255, 255, 255),
                        trailingIconBg = Color.rgb(200, 60, 60),
                        trailingIcon = AppIcons.Enter,
                        iconTransform = "rotate(180deg)",
                        iconFilter = "brightness(0) invert(1)",
                        onClick = onLeaveClick,
                    )
                }
            } else {
                ActionPill(
                    text = t.eventStart,
                    bg = sportColors.primary,
                    textColor = sportColors.onPrimary,
                    trailingIconBg = sportColors.secondary,
                    trailingIcon = AppIcons.ArrowUpOutline,
                    iconTransform = "rotate(45deg)",
                    iconFilter = sportColors.iconFilterOnSecondary,
                    onClick = onStartClick,
                )
            }
        }
        state.userParticipation == UserParticipation.CONFIRMED ||
            state.userParticipation == UserParticipation.PENDING -> {
            // Pending participants keep Leave so they can withdraw their
            // request; the "Pending approval" badge lives in the title row
            // (under the PRO badge), not next to this action.
            ActionPill(
                text = t.eventLeave,
                bg = Color.rgb(242, 85, 85),
                textColor = Color.rgb(255, 255, 255),
                trailingIconBg = Color.rgb(200, 60, 60),
                trailingIcon = AppIcons.Enter,
                iconTransform = "rotate(180deg)",
                iconFilter = "brightness(0) invert(1)",
                onClick = onLeaveClick,
            )
        }
        else -> {
            ActionPill(
                text = t.eventJoin,
                bg = sportColors.primary,
                textColor = sportColors.onPrimary,
                trailingIconBg = sportColors.secondary,
                trailingIcon = AppIcons.ArrowUpOutline,
                iconTransform = "rotate(45deg)",
                iconFilter = sportColors.iconFilterOnSecondary,
                onClick = onJoinClick,
            )
        }
    }
}

@Composable
private fun ActionPill(
    text: String,
    bg: Color,
    textColor: Color,
    trailingIconBg: Color,
    trailingIcon: String,
    iconTransform: String,
    iconFilter: String,
    onClick: () -> Unit,
) {
    Row(
        modifier = Modifier
            .sHeight(44)
            .sBorderRadius(100)
            .backgroundColor(bg)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .sPaddingLeft(36)
            .sPaddingRight(4)
            .sPaddingTop(4)
            .sPaddingBottom(4)
            .sGap(12),
        verticalAlignment = Alignment.CenterVertically
    ) {
        SpanText(
            text,
            modifier = Modifier.bodyLMedium().color(textColor)
                .styleModifier {
                    property("white-space", "nowrap")
                    property("letter-spacing", s(0.032))
                }
        )
        Box(
            modifier = Modifier
                .sSize(36)
                .sBorderRadius(100)
                .overflow(Overflow.Hidden)
                .backgroundColor(trailingIconBg),
            contentAlignment = Alignment.Center
        ) {
            Icon(
                src = trailingIcon,
                modifier = Modifier.styleModifier {
                    property("transform", iconTransform)
                    property("filter", iconFilter)
                }
            )
        }
    }
}

@Composable
fun EventDetailStatusBadge(status: EventStatus) {
    val t = LocalAppStrings.current
    val bgColor: Color
    val textColor: Color
    val label: String

    when (status) {
        EventStatus.NEW -> {
            bgColor = Color.rgb(228, 205, 64)
            textColor = Color.rgb(0, 0, 0)
            label = t.eventStatusPending
        }
        EventStatus.IN_PROGRESS -> {
            bgColor = Color.rgb(232, 137, 8)
            textColor = Color.rgb(0, 0, 0)
            label = t.eventStatusLive
        }
        EventStatus.COMPLETED -> {
            bgColor = Color.rgb(52, 213, 36)
            textColor = Color.rgb(0, 0, 0)
            label = t.eventCompletedBadge
        }
        EventStatus.CANCELED -> {
            bgColor = Color.rgb(242, 85, 85)
            textColor = Color.rgb(255, 255, 255)
            label = t.eventStatusCanceled
        }
    }

    Box(
        modifier = Modifier
            .backgroundColor(bgColor)
            .sBorderRadius(22)
            .sPadding(8, 22),
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            label,
            modifier = Modifier
                .bodyLMedium()
                .color(textColor)
                .styleModifier { property("white-space", "nowrap") }
        )
    }
}

@Composable
private fun IconActionButton(
    iconSrc: String,
    onClick: () -> Unit = {}
) {
    Box(
        modifier = Modifier
            .sSize(44)
            .cursor(Cursor.Pointer)
            .onClick { onClick() },
        contentAlignment = Alignment.Center
    ) {
        Icon(src = iconSrc)
    }
}

/**
 * Inline "Pending approval" badge shown next to the Leave action when the
 * user has requested to join but is still awaiting admin confirmation.
 * Pulsing amber dot + label so the state is obvious at a glance.
 */
@Composable
private fun PendingApprovalBadge() {
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier
            .sHeight(44)
            .sBorderRadius(100)
            .sPadding(0, 16)
            .styleModifier {
                property("background-color", "rgba(255, 184, 0, 0.12)")
                property("border", "1px solid rgba(255, 184, 0, 0.45)")
                property("box-sizing", "border-box")
                property("white-space", "nowrap")
            }
            .sGap(10),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(
            modifier = Modifier
                .sSize(8)
                .borderRadius(50.percent)
                .styleModifier {
                    property("background-color", "#FFB800")
                    property("box-shadow", "0 0 8px rgba(255, 184, 0, 0.6)")
                    property("animation", "pp-pending-dot 1.4s ease-in-out infinite")
                }
        )
        SpanText(
            t.eventPendingApprovalBadge,
            modifier = Modifier
                .sFontSize(14)
                .fontWeight(FontWeight.Medium)
                .color(Color.rgb(255, 216, 107))
                .styleModifier {
                    property("font-family", "Urbanist, sans-serif")
                    property("line-height", s(18))
                    property("letter-spacing", s(0.028))
                },
        )
    }
    androidx.compose.runtime.LaunchedEffect(Unit) {
        val styleId = "pp-pending-dot-style"
        if (kotlinx.browser.document.getElementById(styleId) == null) {
            val styleEl = kotlinx.browser.document.createElement("style")
            styleEl.id = styleId
            styleEl.textContent = """
                @keyframes pp-pending-dot {
                  0%, 100% { opacity: 1;   transform: scale(1); }
                  50%      { opacity: 0.4; transform: scale(0.8); }
                }
            """.trimIndent()
            kotlinx.browser.document.head?.appendChild(styleEl)
        }
    }
}
